import { useEffect, useRef, useState } from "react"
import { DraggableLocation } from "react-beautiful-dnd";
import cloneDeep from 'lodash/cloneDeep';

import { AsyncStatus, DraggableType, FilterType, StageState, UpdateState } from "enums"
import { IFilter, IStage, IUpdate } from "interfaces";
import { FiltersService, IGetAffectedResponse, IGetAffectedResult, LocationsService, ManufacturersService, parseAxiosError, PubSub, TemplatesService, UpdatesService, UpdateTypesService, UsersService } from "services";
import { isValidDate, Stale } from "services/Utils";
import { DateTime } from "luxon";

export enum APICallType {
    Approve = 'Approve',
    Cancel = 'Cancel',
    ChangeSchedule = 'ChangeSchedule',
    ConfirmStage = 'ConfirmStage',
    Delete = 'Delete',
    RevertToDraft = 'RevertToDraft',
    Save = 'Save',
    SubmitForApproval = 'SubmitForApproval',
}

export interface ICheck {
    isPassing: boolean,
    isRequired: boolean,
    message: string,
}

export class UpdatePageModel {
    public apiCallStatus = AsyncStatus.Initial;
    public apiCallType: APICallType;
    public apiCallError = '';

    public loadStatus = AsyncStatus.Loading;
    public loadError = '';
    public hasLoadedInitially = false;

    private affectedStale = new Stale();
    public affectedStatus = AsyncStatus.Initial;
    public affectedError: string;

    public affected: IGetAffectedResponse;
    public affectedByFilterId: {[id: number]: IGetAffectedResult} = {};
    public affectedByStageId: {[id: number]: IGetAffectedResult} = {};

    public isApprover: boolean;
    public isChangingSchedule = false;
    public scheduleChecks: { [stageId: number]: ICheck[] } = {};
    private updateBeforeScheduleChange: IUpdate;
    public update: IUpdate;

    // Since we're just storing the rollout as a json blob in the db, the frontend generates the id's.
    private nextFilterId = 1;
    private nextStageId = 1;
    
    // When something changes, we'll publish an event. Hooks can use this to trigger a rerender.
    public pubSubOnChange = new PubSub<void>();

    // ------------------- API Calls -------------------

    public load = (isNew: boolean, updateId: number, templateID: number, templateLocations: string[], startAt?: Date): Promise<any> => {
        this.loadStatus = AsyncStatus.Loading
        this.loadError = '';

        const isApproverP = UsersService.getIsApprover();
        const locationAttrsP = LocationsService.getAttributes();

        let updateP: Promise<IUpdate>;
        if (!isNew) {
            updateP = UpdatesService.getUpdate(updateId);
        } else {
            let stage1 = this.createDefaultStage({isFirst: true})

            if(startAt){
                const startAtDt = DateTime.fromJSDate(startAt);
                stage1.startTime = startAt
                stage1.stopTime = startAtDt.plus({days: 7}).toJSDate()
                // overwrite specified stop time with template duration
                if (templateID > 0) {
                    const t = TemplatesService.allTemplatesMap[templateID];
                    if (t) stage1.stopTime = startAtDt.plus({milliseconds: t.duration}).toJSDate();
                }
            } else {
                // overwrite default stop time with template duration
                if (templateID > 0) {
                    const startAtDt = DateTime.fromJSDate(stage1.startTime);
                    const t = TemplatesService.allTemplatesMap[templateID];
                    if (t) stage1.stopTime = startAtDt.plus({milliseconds: t.duration}).toJSDate();
                }
            }

            let payload: IUpdate = {
                id: undefined,
                deviceModelIds: [],
                locations: [],
                manufacturerId: undefined,
                name: 'New Update',
                notificationEmails: [],
                rollout: {
                    global: {
                        filters: [],
                    },
                    stages: [stage1],
                },
                serialNumbers: [],
                state: UpdateState.Draft,
                counts: undefined,
                startAt: undefined,
                stopAt: undefined,
                isPreApproved: false,
            }

            if (templateID > 0) {
                const t = TemplatesService.allTemplatesMap[templateID];
                if (t) {
                    let locs = templateLocations.length > 3 ? templateLocations.slice(0, 3).join(", ") : templateLocations.join(", ")
                    payload.name = `${t.name}: ${locs}`;
                    payload.locations = templateLocations
                    payload.isPreApproved = true;
                    payload.manufacturerId = t.manufacturerID;
                    payload.updateTypeId = t.updateTypeID;
                    payload.updateFileId = t.updateFileID;
                    payload.deviceModelIds = t.deviceModelIDs;
                    payload.notificationEmails = t.notifiers;
                    payload.rollout.global.filters = [{
                        id: 1,
                        type: FilterType.Locations,
                        locations: templateLocations,
                    }]
                }
            }

            // Create a new update immediately so that we can autosave when the user makes changes.
            updateP = UpdatesService.postUpdate(payload)
                .then((response) => {
                    return UpdatesService.getUpdate(response.id);
                });
        }

        return Promise.all([updateP, isApproverP, locationAttrsP])
            .then(([u, isApprover, locationAttrs]) => {
                this.hasLoadedInitially = true;
                this.isApprover = isApprover;
                this.loadStatus = AsyncStatus.Success;
                this.onLoadSuccess(u);
            })
            .catch((err) => {
                this.loadStatus = AsyncStatus.Error;
                this.loadError = parseAxiosError(err);
                this.pubSubOnChange.publish();
            });
    }

    public save = (onErrorRevert?: IUpdate): Promise<void> => {
        return this.doAPICallThenGet(APICallType.Save, () => {
            return UpdatesService.putUpdate(this.update);
        }, onErrorRevert);
    }

    public submitForApproval() {
        return this.doAPICallThenGet(APICallType.SubmitForApproval, () => {
            return UpdatesService.sendForApproval(this.update.id);
        });
    }

    public approve() {
        return this.doAPICallThenGet(APICallType.Approve, () => {
            return UpdatesService.approve(this.update.id);
        });
    }

    public revertToDraft() {
        return this.doAPICallThenGet(APICallType.RevertToDraft, () => {
            return UpdatesService.revertToDraft(this.update.id);
        });
    }

    public cancel() {
        return this.doAPICallThenGet(APICallType.Cancel, () => {
            return UpdatesService.cancelUpdate(this.update.id);
        });
    }

    public delete = (): Promise<void> => {
        this.apiCallStatus = AsyncStatus.Loading;
        this.apiCallType = APICallType.Delete;
        this.apiCallError = '';
        this.pubSubOnChange.publish();

        const p = UpdatesService.deleteUpdate(this.update.id)
            .then(() => {
                this.apiCallStatus = AsyncStatus.Success;
                this.pubSubOnChange.publish();
            })
        p.catch((err) => {
            this.apiCallStatus = AsyncStatus.Error;
            this.apiCallError = parseAxiosError(err);
            this.pubSubOnChange.publish();
        });
        return p;
    }

    // Internal method to do an API call, then re-get the update.
    // It supports reverting the update to a previous state if the call fails.
    private doAPICallThenGet(type: APICallType, doCall: () => Promise<any>, onErrorRevert?: IUpdate): Promise<any> {
        this.apiCallStatus = AsyncStatus.Loading;
        this.apiCallType = type;
        this.apiCallError = '';
        this.pubSubOnChange.publish();

        const p = doCall()
            .then(() => {
                return UpdatesService.getUpdate(this.update.id)
                    .then((u) => {
                        this.apiCallStatus = AsyncStatus.Success;
                        this.onLoadSuccess(u);
                    })
            });
        p.catch((err) => {
            if (onErrorRevert) {
                this.update = onErrorRevert;
            }

            this.apiCallStatus = AsyncStatus.Error;
            this.apiCallError = parseAxiosError(err);
            // We might have rolled back to a previous version of the update, so we need to refresh the counts.
            this.getAffected(this.update);
        });
        return p;
    }

    private onLoadSuccess(u: IUpdate) {
        this.update = u;
        this.calcNextCardIds(u);
        this.checkAndFixIds(u);
        this.scheduleChecks = this.calcScheduleChecks(u.rollout.stages);

        // Make sure first stage still doesn't show as requiring confirmation
        const firstStage = u.rollout.stages[0];
        if (firstStage) {
            firstStage.requiresConfirmation = false;
        }

        this.getAffected(u);
    }

    private resetGetAffected() {
        this.affectedStatus = AsyncStatus.Initial;
        this.affectedError = undefined;
        this.affected = undefined;
        this.affectedByFilterId = {};
        this.affectedByStageId = {};
    }

    private getAffected(u: IUpdate) {
        const callId = this.affectedStale.startCall();
        this.affectedStatus = AsyncStatus.Loading;
        this.pubSubOnChange.publish();

        FiltersService.getAffected(u.manufacturerId, u.deviceModelIds, u.rollout)
            .then((response) => {
                if (this.affectedStale.isStale(callId)) return;

                this.resetGetAffected();
                this.affected = response;
                // Put all filter and stage counts into maps for easier lookups
                Object.assign(this.affectedByFilterId, response.rollout.global.filters);
                
                for (const stage of response.rollout.stages) {
                    this.affectedByStageId[stage.id] = stage.result;
                    Object.assign(this.affectedByFilterId, stage.filters);
                }

                this.affectedStatus = AsyncStatus.Success;
                this.pubSubOnChange.publish();
            })
            .catch((err) => {
                if (this.affectedStale.isStale(callId)) return;

                this.resetGetAffected();
                this.affectedStatus = AsyncStatus.Error;
                this.affectedError = parseAxiosError(err);
                this.pubSubOnChange.publish();
            })
    }

    // ------------------- Schedule Change and Confirm -------------------

    public startScheduleChange = () => {
        this.isChangingSchedule = true;
        // Preserve the current state in case the user cancels changing the schedule.
        this.updateBeforeScheduleChange = cloneDeep(this.update);
        this.pubSubOnChange.publish();
    }

    public cancelScheduleChange = () => {
        if (!this.isChangingSchedule) return;

        this.isChangingSchedule = false;
        this.update = this.updateBeforeScheduleChange;
        this.updateBeforeScheduleChange = undefined;
        this.scheduleChecks = this.calcScheduleChecks(this.update.rollout.stages);
        this.pubSubOnChange.publish();
    };

    public saveScheduleChange = (): Promise<any> => {
        return this.doAPICallThenGet(APICallType.ChangeSchedule, () => {
            return UpdatesService.changeSchedule(this.update)
                // We'll pass our own callbacks here to cleanup changing the schedule before we do the Get.
                .then(() => {
                    this.isChangingSchedule = false;
                    this.updateBeforeScheduleChange = undefined;
                });
        })
    }

    public canConfirmStage = (update: IUpdate, stage: IStage, stageIndex: number): boolean => {
        let prevStage: IStage;
        if (stageIndex >= 1) {
            prevStage = update?.rollout.stages[stageIndex - 1];
        }

        if (!update || !stage || !prevStage) {
            return false;
        }

        return update.state === UpdateState.Running
            && stage.state === StageState.Scheduled
            && stage.requiresConfirmation
            && !stage.isConfirmed
            && !this.isChangingSchedule
            // Make sure the previous stage is running or done before we allow confirming this one
            && (
                prevStage.state === StageState.Running
                || prevStage.state === StageState.Done
            )

    }

    public confirmStage = (stageIndex: number): Promise<any> => {
        return this.doAPICallThenGet(APICallType.ConfirmStage, () => {
            return UpdatesService.confirmStage(this.update.id, stageIndex)
        });
    }

    // ------------------- Stages and Filters -------------------

    public saveStage = (stage: IStage): Promise<any> => {
        // Make a copy of the update so we can rollback if an error occurs
        const updateBefore = cloneDeep(this.update);

        // If it's an existing stage, replace it.
        const index = this.update.rollout.stages.findIndex(s => s.id === stage.id);
        if (index !== -1) {
            this.update.rollout.stages[index] = stage;
        } else {
            // It's a new stage, append it
            this.update.rollout.stages.push(stage);
        }

        return this.save(updateBefore);
    }

    public async changeStageScheduleLocalOnly(stage: IStage): Promise<any> {
        // If it's an existing stage, replace it.
        const index = this.update.rollout.stages.findIndex(s => s.id === stage.id);
        if (index !== -1) {
            this.update.rollout.stages[index] = stage;
        } else {
            throw `Can't find stage with id: ${stage.id}`;
        }

        this.scheduleChecks = this.calcScheduleChecks(this.update.rollout.stages);
        this.pubSubOnChange.publish();
    }

    public deleteStage = (id: number): Promise<any> => {
        const updateBefore = cloneDeep(this.update);

        // If it's an existing stage, replace it.
        const index = this.update.rollout.stages.findIndex(s => s.id === id);
        if (index !== -1) {
            this.update.rollout.stages.splice(index, 1);
        } else {
            return Promise.reject(`Can't find stage to delete with id: ${id}`);
        }

        return this.save(updateBefore);
    }

    public saveExistingFilter = ({ filter, stageId, isGlobal }: {
        filter: IFilter,
        stageId: number,
        isGlobal: boolean,
    }): Promise<any> => {
        const updateBefore = cloneDeep(this.update);
        
        const { filterArray, index } = this.findFilter({
            filterId: filter.id,
            stageId: stageId,
            isGlobal: isGlobal,
        });
        if (index === -1) {
            return Promise.reject(`Can't find filter to save`);
        }

        // Replace the existing filter
        filterArray[index] = filter;

        // Save
        return this.save(updateBefore);
    }

    public saveNewFilter = ({ filter, stageId, isGlobal }: {
        filter: IFilter,
        stageId: number,
        isGlobal: boolean,
    }): Promise<any> => {
        const updateBefore = cloneDeep(this.update);

        let filtersArr = this.update.rollout.global.filters;
        if (!isGlobal) {
            const stage = this.update.rollout.stages.find(s => s.id === stageId);
            filtersArr = stage?.filters;
        }
        if (!filtersArr) {
            return Promise.reject(`Couldn't find array in which to insert filter.`)
        }
    
        // Insert the filter at the end
        filtersArr.push(filter);

        // Save
        return this.save(updateBefore);
    }

    public deleteFilter = ({ filterId, isGlobal, stageId }: {
        filterId: number,
        stageId: number,
        isGlobal: boolean,
    }): Promise<any> => {
        const updateBefore = cloneDeep(this.update);

        const { filterArray, index } = this.findFilter({
            filterId: filterId,
            isGlobal: isGlobal,
            stageId: stageId,
        })
        if (index === -1) {
            return Promise.reject(`Couldn't find filter to delete.`)
        }

        filterArray.splice(index, 1);

        return this.save(updateBefore);
    }

    public duplicateFilter = ({ filterId, isGlobal, stageId }: {
        filterId: number,
        stageId: number,
        isGlobal: boolean,
    }): Promise<any> => {
        const updateBefore = cloneDeep(this.update);

        const { filter, filterArray, index } = this.findFilter({
            filterId: filterId,
            isGlobal: isGlobal,
            stageId: stageId,
        })
        if (index === -1) {
            return Promise.reject(`Couldn't find filter to duplicate.`)
        }

        // Clone the filter and give it a new id.
        const newFilter = cloneDeep(filter);
        newFilter.id = this.getNewFilterId();

        // Insert immediately after this filter
        filterArray.splice(index + 1, 0, newFilter);

        return this.save(updateBefore);
    }

    public moveStage(stageId: number, source: DraggableLocation, destination: DraggableLocation): Promise<any> {
        const updateBefore = cloneDeep(this.update);
        const stages = this.update.rollout.stages;
        // Find the stage. We could use source.index, but it's slightly safer to just look it up in case it changed.
        const sourceIndex = stages.findIndex(stage => stage.id === stageId);
        if (sourceIndex !== -1) {
            const [stage] = stages.splice(sourceIndex, 1);
            stages.splice(destination.index, 0, stage);

            return this.save(updateBefore);
        } else {
            return Promise.reject('Couldnt find stage to move with id: ' + stageId);
        }
    }

    public moveFilter(filterId: number, source: DraggableLocation, destination: DraggableLocation): Promise<any> {
        const updateBefore = cloneDeep(this.update);
        const isSourceGlobal = (source.droppableId === 'global');
        const isDestGlobal = (destination.droppableId === 'global');
        
        // Get arrays for source/destination
        const sourceFilters = this.findFilterArray({
            isGlobal: isSourceGlobal,
            stageId: Number(source.droppableId),
        })
        if (!sourceFilters) {
            return Promise.reject(`Couldn't find filter source array`);
        }
        const destFilters = this.findFilterArray({
            isGlobal: isDestGlobal,
            stageId: Number(destination.droppableId),
        })
        if (!destFilters) {
            return Promise.reject(`Couldn't find filter destination array`);
        }

        // Find index for delete
        const sourceIndex = sourceFilters.findIndex(f => f.id === filterId);
        if (sourceIndex === -1) {
            return Promise.reject(`Couldn't find filter in source array`);
        }

        // Remove from source, insert in destination
        const [filter] = sourceFilters.splice(sourceIndex, 1);
        destFilters.splice(destination.index, 0, filter);

        return this.save(updateBefore);
    }

    // Helper function to lookup a filter and its array.
    private findFilter({ filterId, isGlobal, stageId }: {
        filterId: number,
        isGlobal?: boolean,
        stageId?: number
    }): {
        filter: IFilter,
        filterArray: IFilter[],
        index: number,
    } {
        const out = {filter: undefined, filterArray: undefined, index: -1};

        // Find the array that the filter lives in
        const arr = this.findFilterArray({isGlobal: isGlobal, stageId: stageId});
        if (!arr) {
            return out;
        }

        // Find the filter in the array
        const index = arr.findIndex(f => f.id === filterId);
        if (index === -1) {
            return out;
        }

        return {
            filter: arr[index],
            filterArray: arr,
            index: index,
        };
    }

    private findFilterArray({ isGlobal, stageId }: {isGlobal: boolean, stageId: number}): IFilter[] {
        if (isGlobal) {
            return this.update.rollout.global.filters;
        }
        const stage = this.update.rollout.stages.find(stage => stage.id === stageId);
        return stage?.filters;
    }

    // ------------------- Data and Mapping -------------------

    public createDefaultFilter(): IFilter {
        return {
            id: this.getNewFilterId(),
            type: FilterType.Locations,
            locations: [],
        }
    }

    public createDefaultStage({ isFirst }: {isFirst: boolean}): IStage {

        // Default to starting 1 week from today at 5:00am.
        const startDt = DateTime.now().plus({days: 7}).set({
            hour: 5,
            minute: 0,
            second: 0,
            millisecond: 0,
        });
        const start = startDt.toJSDate();
        // Default duration is 7 days
        const stop = startDt.plus({days: 7}).toJSDate();

        return {
            id: this.getNewStageId(),
            state: StageState.Scheduled,
            startTime: start,
            stopTime: stop,
            filters: [],
            // The first stage can't require confirmation.
            // All subsequent stages should default to requiring it.
            requiresConfirmation: !isFirst, 
        }
    }

    // We give each card an id. We don't want to conflict with existing ids, so init our id to 1 more than the max card id.
    private calcNextCardIds(u: IUpdate) {
        let maxFilterId = 0;
        let maxStageId = 0;

        if (u.rollout) {
            if (u.rollout.global?.filters) {
                // Check global filters
                for (const f of u.rollout.global.filters) {
                    if (f.id && f.id > maxFilterId) {
                        maxFilterId = f.id;
                    }
                }                
            }
            if (u.rollout.stages) {
                for (const stage of u.rollout.stages) {
                    // Check stage id
                    if (stage.id && stage.id > maxStageId) {
                        maxStageId = stage.id;
                    }
                    // Check stage filters
                    if (stage.filters) {
                        for (const f of stage.filters) {
                            if (f.id && f.id > maxFilterId) {
                                maxFilterId = f.id;
                            }
                        }
                    }
                }
            }
        }

        this.nextFilterId = maxFilterId + 1;
        this.nextStageId = maxStageId + 1;
    }

    // The frontend is responsible for creating stage and filter ids.
    // However, we want to make sure that somehow the ids aren't missing,
    // and that there aren't any duplicates.
    private checkAndFixIds(u: IUpdate) {
        const stagesById = {};
        const filtersById = {};

        const checkFilter = (filter: IFilter) => {
            if (!filter.id) {
                console.error('Global filter is missing id: ', filter)
                filter.id = this.getNewFilterId();
            }
            if (filtersById[filter.id]) {
                console.error('Duplicate filter id: ', filter);
                filter.id = this.getNewFilterId();
            }
            filtersById[filter.id] = filter;
        }

        u.rollout.global.filters.forEach(checkFilter);

        for (const stage of u.rollout.stages) {
            if (!stage.id) {
                console.error('Missing stage id: ', stage);
                stage.id = this.getNewStageId();
            }
            if (stagesById[stage.id]) {
                console.error('Duplicate stage id: ', stage);
                stage.id = this.getNewStageId();
            }
            stagesById[stage.id] = stage;

            stage.filters.forEach(checkFilter);
        }
    }

    public getNewFilterId(): number {
        const id = this.nextFilterId;
        this.nextFilterId++;
        return id;
    }

    public getNewStageId(): number {
        const id = this.nextStageId;
        this.nextStageId++;
        return id;
    }

    // We're doing some validation on the frontend for stages around the scheduling.
    private calcScheduleChecks(stages: IStage[]): {[stageId: number]: ICheck[]} {
        const out = {};
        const now = new Date();

        for (const s of stages) {
            out[s.id] = [];

            // Make sure we have start/stop dates for each stage. This should always be true, but just in case.
            if (!isValidDate(s.startTime) || !isValidDate(s.stopTime)) {
                out[s.id].push({
                    isPassing: false,
                    isRequired: true,
                    message: 'Missing start or stop time.',
                });
                // If we don't have a date, then the we can't perform other checks, so go ahead and quit.
                return out;
            }
        }

        // Now we're guaranteed every stage has start/stop. Do schedule checks.
        // The board only displays the first failing check, so these are sorted by priority.
        for (let i = 0; i < stages.length; i++) {
            const s = stages[i]
            const checks = out[s.id];

            // Using try/catch because the date functions can throw on invalid input
            try {
                // Can't overlap other stages
                const checkOverlap: ICheck = {
                    isPassing: true,
                    isRequired: true,
                    message: `Doesn't overlap other stages.`,
                }
                checks.push(checkOverlap);
                if (i > 0) {
                    const prev = stages[i - 1];
                    if (s.startTime < prev.stopTime) {
                        checkOverlap.isPassing = false;
                        checkOverlap.message = `Must start after Stage ${i}.`;
                    }
                }

                // Duration
                checks.push({
                    isPassing: (s.stopTime.getTime() - s.startTime.getTime()) >= (1000 * 60 * 60 * 24),
                    isRequired: false,
                    message: 'Duration is less than 24 hours.',
                })

                // A scheduled stage must run for at least 24 hours from now.
                const nowPlus24Hours = DateTime.now().plus({hours: 24});
                checks.push({
                    isPassing: s.state !== StageState.Scheduled || s.stopTime.valueOf() >= nowPlus24Hours.valueOf(),
                    isRequired: false,
                    message: `A scheduled stage's end is less than 24 hours in the future.`
                })
            } catch (err) {
                console.error(err);
                checks.push({
                    isPassing: false,
                    isRequired: false,
                    message: 'Error checking stage: ' + err,
                })
            }
        }
        return out;
    }

    // ------------------- Drag and Drop -------------------
    // React-beautiful-dnd uses string id's for each draggable and droppable.
    // We're storing the type and id into that id, so that we can parse them out later.
    // This is necessary so that we can tell whether a filter or a stage is being dragged.
    public getDragDropId(type: DraggableType, id: number): string {
        return type + '_' + id;
    }

    public parseDragDropId(dragDropId: string): {type?: DraggableType, id?: number, isValid: boolean} {
        const result = {type: undefined, id: undefined, isValid: false};
        if (!(dragDropId?.length > 0)) {
            return result;
        }

        // split into type and id
        const parts = dragDropId.split('_')
        if (parts.length !== 2) {
            return result;
        }
        result.type = parts[0];
        result.id = Number(parts[1]);
        result.isValid = result.type && result.id > 0;

        return result;
    }

}

// ------------------- Hooks -------------------

// Wraps a ReportsModel in hooks so that when something changes, the component will rerender.
export function useUpdatePageModel(): {model: UpdatePageModel} {
    const modelRef = useRef<UpdatePageModel>();
    if (!modelRef.current) {
        modelRef.current = new UpdatePageModel();
    }
    const model = modelRef.current;

    // Instead of creating hooks versions of all the model methods,
    // we'll just use the model as the source of truth and set our state when it changes, which will cause a rerender.
    const [state, setState] = useState({model: model});

    useEffect(() => {
        const unsubscribe = model.pubSubOnChange.subscribe(() => {
            setState({model: model});
        })

        return unsubscribe;
    });

    return {model: model};
}
